import { ApiClient } from "@/services/apiClient/ApiClient";
import { ProfileDataSavingException } from "@/services/user/settings/exceptions";
import { type UserSettings, type UpdateUserSettingsData } from "@/services/user/settings/types";
import type { AxiosError } from "axios";
import { gettext } from "@/services/translation";
import { useUserSettings } from "@/composables/user/userSettings";
import { UserCollector_Schema } from "@/services/user/settings/userSettingsSchema";
import { UserBankInfo_Schema } from "@/services/user/bankInfo/bankInfoSchema";
import { Address_Schema } from "@/services/addresses/addressSchema";
import type { z } from "zod";

export const updateUserSettings = async (data: UpdateUserSettingsData): Promise<void> =>
  ApiClient.put<UserSettings>("api/user/collector", {}, data)
    .then(({ data: updatedSettings }) => {
      const { userSettings } = useUserSettings();
      userSettings.value = updatedSettings;
    })
    .catch((error: AxiosError) => {
      const status = error.response?.status;
      if (status === 400) {
        const msg = gettext.$gettext("Nastavení se nepodařilo uložit.");
        throw new ProfileDataSavingException(msg);
      } else if (status === 404) {
        const msg = gettext.$gettext("Tohoto uživatele neznáme.");
        throw new ProfileDataSavingException(msg);
      } else if (status === 409) {
        const data = error.response?.data as { error: string };
        const type = data.error;
        let msg = "Unknown error.";
        if (type && type === "nickname_taken") {
          msg = gettext.$gettext("Bohužel, přezdívku už používá někdo jiný. Zkus nějakou jinou.");
        } else if (type && type === "nickname_too_early_change") {
          msg = gettext.$gettext("Přezdívku zatím nelze změnit.");
        }

        throw new ProfileDataSavingException(msg);
      }

      throw error;
    });

const UserSettingsResponse_Schema = UserCollector_Schema.extend({
  paymentInfo: UserBankInfo_Schema.array(),
  personalAddresses: Address_Schema.array(),
})
  .meta({ openapi: "UserSettingsResponse" })
  .transform(({ paymentInfo, personalAddresses, holidayUntil, ...rest }) => ({
    collector: { ...rest, absenceUntil: holidayUntil ?? null },
    bankInfo: paymentInfo.at(-1),
    address: personalAddresses.at(0),
  }));

export type UserSettingsResponse = z.infer<typeof UserSettingsResponse_Schema>;

export const fetchUserSettings = async (): Promise<UserSettingsResponse> =>
  ApiClient.get("api/user/settings")
    .then(({ data }) => UserSettingsResponse_Schema.parse(data))
    .catch((error) => {
      const status = error.response?.status;
      const UserNotEstablishedStatuses = [400, 404];
      if (UserNotEstablishedStatuses.includes(status)) {
        throw new Error(`User has no collector record. (HTTP ${status})`);
      } else {
        throw error;
      }
    });
